import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  User,
  Users,
  AlertOctagon,
  Award,
  Database,
  Lock,
  CloudLightning,
  ExternalLink,
  BookOpen,
  Scale,
  LogOut,
  Settings,
  Mail,
  Globe,
  Copy,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  FileText,
  Calendar,
  Check,
  HelpCircle,
  Info
} from "lucide-react";
import Footer from "../components/Footer";
import "./terms-and-conditions.css";

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState("sec1");
  const [copied, setCopied] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);


  // 17 Sections data for Table of Contents
  const sections = [
    { id: "sec1", number: "1", title: "About FastInvoice" },
    { id: "sec2", number: "2", title: "Eligibility" },
    { id: "sec3", number: "3", title: "User Account" },
    { id: "sec4", number: "4", title: "User Responsibilities" },
    { id: "sec5", number: "5", title: "Acceptable Use" },
    { id: "sec6", number: "6", title: "Subscription & Premium" },
    { id: "sec7", number: "7", title: "Intellectual Property" },
    { id: "sec8", number: "8", title: "Your Content" },
    { id: "sec9", number: "9", title: "Data Storage & Security" },
    { id: "sec10", number: "10", title: "Availability of Service" },
    { id: "sec11", number: "11", title: "Third-Party Services" },
    { id: "sec12", number: "12", title: "Disclaimer" },
    { id: "sec13", number: "13", title: "Limitation of Liability" },
    { id: "sec14", number: "14", title: "Suspension & Termination" },
    { id: "sec15", number: "15", title: "Changes to These Terms" },
    { id: "sec16", number: "16", title: "Governing Law" },
    { id: "sec17", number: "17", title: "Contact Us" }
  ];

  // Dynamic scroll spy and scroll progress indicator
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }


    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth"
      });
      setActiveSection(id);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("support@fastinvoice.in");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="terms-policy-page">


      {/* GLOW DECORATIONS */}
      <div className="bg-glow glow-top-right"></div>
      <div className="bg-glow glow-bottom-left"></div>


      {/* HERO BANNER */}
      <section className="policy-hero">
        <div className="policy-hero-content">
          <div className="gst-badge">
            <span className="badge-dot"></span> Made for Indian Businesses • GST Ready
          </div>
          <h1>Terms & Conditions</h1>
          <p className="hero-subtext">
            Please read these terms carefully before accessing or using FastInvoice services.
          </p>
          <div className="meta-info">
            <div className="meta-item">
              <Calendar size={16} />
              <span>Effective Date: <strong>June 29, 2026</strong></span>
            </div>
            <div className="meta-item">
              <ShieldCheck size={16} />
              <span>Last Updated: <strong>June 29, 2026</strong></span>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT SECTION WITH SIDEBAR */}
      <main className="policy-main-container">
        <div className="policy-layout">
          
          {/* LEFT SIDEBAR - TABLE OF CONTENTS */}
          <aside className="policy-sidebar">
            <div className="sidebar-sticky-wrapper">
              <h3>Terms Outline</h3>
              <ul className="toc-list">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      className={`toc-link ${activeSection === section.id ? "active" : ""}`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="toc-num">{section.number}</span>
                      <span className="toc-title">{section.title}</span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="sidebar-cta">
                <h4>Legal Questions?</h4>
                <p>Contact our support team directly for compliance inquiries.</p>
                <a href="mailto:support@fastinvoice.in" className="cta-email-btn">
                  <Mail size={16} />
                  Email Support
                </a>
              </div>
            </div>
          </aside>

          {/* RIGHT COLUMN - POLICY DETAILS */}
          <article className="policy-content">
            
            {/* INTRO */}
            <div className="policy-intro-card">
              <p>
                Welcome to <strong>FastInvoice</strong>. These Terms &amp; Conditions ("Terms") govern your access to and use of the FastInvoice website, mobile application, and related services. By accessing or using FastInvoice, you agree to be bound by these Terms. If you do not agree, please discontinue using our services.
              </p>
            </div>

            {/* SECTION 1: About FastInvoice */}
            <section id="sec1" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Info className="sec-icon" />
                </div>
                <h2>1. About FastInvoice</h2>
              </div>
              <p>
                FastInvoice is an online invoicing platform designed to help individuals and businesses create, manage, and share professional business documents, including but not limited to:
              </p>
              
              <div className="features-grid">
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Invoices & Estimates</strong>
                    <p>Create and share detailed customer billing formats</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Quotations & Receipts</strong>
                    <p>Issue proof of payments and structural quotations</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Purchase Orders</strong>
                    <p>Send standard transactional purchase document outlines</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Business Documents</strong>
                    <p>Simplify customer record files and commercial reports</p>
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 2: Eligibility */}
            <section id="sec2" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Users className="sec-icon" />
                </div>
                <h2>2. Eligibility</h2>
              </div>
              <p>
                You must be at least 18 years old or have legal permission from a parent or guardian to use our services.
              </p>
              <div className="highlight-callout secure-callout">
                <p>
                  By using FastInvoice, you represent that you have the legal authority to enter into this agreement and comply with these Terms.
                </p>
              </div>
            </section>

            {/* SECTION 3: User Account */}
            <section id="sec3" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <User className="sec-icon" />
                </div>
                <h2>3. User Account</h2>
              </div>
              <p>
                Some features require creating an account. You agree to:
              </p>
              <ul className="custom-bullets">
                <li>
                  <span className="bullet-indicator"></span>
                  Provide accurate, current, and complete information.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Keep your login credentials secure at all times.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Notify us immediately of any unauthorized account activity.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Accept responsibility for all activities under your account.
                </li>
              </ul>
            </section>

            {/* SECTION 4: User Responsibilities */}
            <section id="sec4" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <BookOpen className="sec-icon" />
                </div>
                <h2>4. User Responsibilities</h2>
              </div>
              <p>
                You are solely responsible for:
              </p>
              <div className="responsibilities-grid">
                <div className="resp-card">
                  <h4>Document Accuracy</h4>
                  <p>The accuracy of invoices, pricing details, and descriptions you generate.</p>
                </div>
                <div className="resp-card">
                  <h4>Customer Info</h4>
                  <p>The validity of customer records and data points you enter into the platform.</p>
                </div>
                <div className="resp-card">
                  <h4>Tax Compliance</h4>
                  <p>Taxes, GST, VAT, and other legal regulatory requirements applicable to your business.</p>
                </div>
                <div className="resp-card">
                  <h4>Backup & Storage</h4>
                  <p>Maintaining secondary backups of important business documents and transaction files.</p>
                </div>
              </div>
              <div className="alert-box info-alert">
                <strong>Important:</strong> FastInvoice does not verify or validate invoice content.
              </div>
            </section>

            {/* SECTION 5: Acceptable Use */}
            <section id="sec5" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <AlertOctagon className="sec-icon" />
                </div>
                <h2>5. Acceptable Use</h2>
              </div>
              <p>
                You agree <strong>not</strong> to:
              </p>
              <ul className="custom-bullets">
                <li>
                  <span className="bullet-indicator-red"></span>
                  Use FastInvoice for illegal, unauthorized, or fraudulent activities.
                </li>
                <li>
                  <span className="bullet-indicator-red"></span>
                  Generate fake, misleading, or deceptive invoices.
                </li>
                <li>
                  <span className="bullet-indicator-red"></span>
                  Upload malware, spyware, viruses, or harmful payload codes.
                </li>
                <li>
                  <span className="bullet-indicator-red"></span>
                  Attempt unauthorized access to our servers or network systems.
                </li>
                <li>
                  <span className="bullet-indicator-red"></span>
                  Copy, modify, reverse engineer, or distribute our software without written permission.
                </li>
              </ul>
              <div className="alert-box note-alert">
                Violation of these Terms may result in immediate suspension or permanent termination of your account.
              </div>
            </section>

            {/* SECTION 6: Subscription & Premium Services */}
            <section id="sec6" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Award className="sec-icon" />
                </div>
                <h2>6. Subscription & Premium Services</h2>
              </div>
              <p>
                FastInvoice may offer both <strong>Free</strong> and <strong>Premium</strong> subscription plans.
              </p>
              <div className="refund-conditions-title">
                Premium features may include:
              </div>
              <div className="premium-features-row">
                <div className="prem-chip"><Check size={14} /> Unlimited invoices</div>
                <div className="prem-chip"><Check size={14} /> PDF export</div>
                <div className="prem-chip"><Check size={14} /> Cloud backup</div>
                <div className="prem-chip"><Check size={14} /> Custom branding</div>
                <div className="prem-chip"><Check size={14} /> Multiple businesses</div>
                <div className="prem-chip"><Check size={14} /> Advanced reports</div>
                <div className="prem-chip"><Check size={14} /> Priority support</div>
              </div>
              <p style={{ marginTop: "16px" }}>
                Subscription fees are charged according to the selected plan. Unless otherwise required by law, subscription payments are non-refundable.
              </p>
            </section>

            {/* SECTION 7: Intellectual Property */}
            <section id="sec7" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <ShieldCheck className="sec-icon" />
                </div>
                <h2>7. Intellectual Property</h2>
              </div>
              <p>
                All software, trademarks, logos, graphics, brand identity, content, and proprietary technology used by <strong>FastInvoice</strong> remain the exclusive property of FastInvoice.
              </p>
              <p>
                You may not reproduce, copy, sell, license, decompile, or distribute any part of our service without prior written consent.
              </p>
            </section>

            {/* SECTION 8: Your Content */}
            <section id="sec8" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <FileText className="sec-icon" />
                </div>
                <h2>8. Your Content</h2>
              </div>
              <p>
                You retain ownership of all invoices, customer information, business details, logo uploads, and other data you create.
              </p>
              <div className="highlight-callout teal-callout">
                <p>
                  By using FastInvoice, you grant us a limited license to store, process, and display your data solely for providing the requested services. We never claim ownership of your business data.
                </p>
              </div>
            </section>

            {/* SECTION 9: Data Storage & Security */}
            <section id="sec9" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Lock className="sec-icon" />
                </div>
                <h2>9. Data Storage & Security</h2>
              </div>
              <p>
                We implement industry-standard security protocols and encryption measures to protect your documents and business databases.
              </p>
              <div className="alert-box note-alert">
                <strong>Attention:</strong> No online platform can guarantee absolute security. You are solely responsible for maintaining backups of your important business files.
              </div>
            </section>

            {/* SECTION 10: Availability of Service */}
            <section id="sec10" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <CloudLightning className="sec-icon" />
                </div>
                <h2>10. Availability of Service</h2>
              </div>
              <p>
                We strive to provide reliable service uptime but do not guarantee uninterrupted platform availability. Uptime may occasionally be impacted by:
              </p>
              <div className="causes-list">
                <div className="cause-item"><span>•</span> Scheduled maintenance & updates</div>
                <div className="cause-item"><span>•</span> Software/platform updates</div>
                <div className="cause-item"><span>•</span> ISP network issues</div>
                <div className="cause-item"><span>•</span> Hardware/technical failure spikes</div>
                <div className="cause-item"><span>•</span> Circumstances beyond our reasonable control</div>
              </div>
            </section>

            {/* SECTION 11: Third-Party Services */}
            <section id="sec11" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <ExternalLink className="sec-icon" />
                </div>
                <h2>11. Third-Party Services</h2>
              </div>
              <p>
                FastInvoice may integrate with third-party software structures, such as payment gateways, transactional email services, cloud storage hosts, and federated authentication providers.
              </p>
              <p>
                We are not responsible for the content, availability, API updates, or compliance policies of any third-party providers.
              </p>
            </section>

            {/* SECTION 12: Disclaimer */}
            <section id="sec12" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <HelpCircle className="sec-icon" />
                </div>
                <h2>12. Disclaimer</h2>
              </div>
              <p>
                FastInvoice is provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis.
              </p>
              <div className="highlight-callout secure-callout">
                <h4>No Warranties Represented</h4>
                <p>
                  We make no warranties regarding accuracy, reliability, availability, fitness for a particular purpose, or error-free operations. Users should always manually verify invoices before dispatching them.
                </p>
              </div>
            </section>

            {/* SECTION 13: Limitation of Liability */}
            <section id="sec13" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Scale className="sec-icon" />
                </div>
                <h2>13. Limitation of Liability</h2>
              </div>
              <p>
                To the maximum extent permitted by law, FastInvoice shall not be liable for loss of profits, business interruptions, lost revenues, loss of business data, tax calculation errors, or indirect and consequential damages.
              </p>
              <div className="alert-box note-alert">
                Your use of the FastInvoice platform is entirely at your own risk.
              </div>
            </section>

            {/* SECTION 14: Suspension & Termination */}
            <section id="sec14" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <LogOut className="sec-icon" />
                </div>
                <h2>14. Suspension & Termination</h2>
              </div>
              <p>
                We reserve the right to suspend or terminate your account if you:
              </p>
              <ul className="custom-bullets">
                <li>
                  <span className="bullet-indicator-red"></span>
                  Violate these Terms &amp; Conditions.
                </li>
                <li>
                  <span className="bullet-indicator-red"></span>
                  Engage in fraudulent or deceptive invoicing activities.
                </li>
                <li>
                  <span className="bullet-indicator-red"></span>
                  Attempt to compromise system servers or compromise security measures.
                </li>
              </ul>
            </section>

            {/* SECTION 15: Changes to These Terms */}
            <section id="sec15" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Settings className="sec-icon" />
                </div>
                <h2>15. Changes to These Terms</h2>
              </div>
              <p>
                We may update these Terms from time to time. The latest version will always be available on our website. Continued use of FastInvoice after changes become effective constitutes acceptance of the revised Terms.
              </p>
            </section>

            {/* SECTION 16: Governing Law */}
            <section id="sec16" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Database className="sec-icon" />
                </div>
                <h2>16. Governing Law</h2>
              </div>
              <p>
                These Terms shall be governed, construed, and interpreted in accordance with the laws of <strong>India</strong>, without regard to its conflict of law principles.
              </p>
            </section>

            {/* SECTION 17: Contact Us */}
            <section id="sec17" className="content-section contact-section-card">
              <div className="section-header">
                <div className="header-icon-box">
                  <Mail className="sec-icon" />
                </div>
                <h2>17. Contact Us</h2>
              </div>
              <p>
                If you have any questions regarding subscriptions, cancellations, billing, or platform terms, please reach out to us:
              </p>

              <div className="contact-details-box">
                <div className="company-meta">
                  <h3>FastInvoice</h3>
                  <p>Invoicing & Inventory Management Solutions</p>
                </div>

                <div className="contact-links-grid">
                  <div className="contact-card-item">
                    <Globe className="contact-card-icon" />
                    <div>
                      <span className="contact-card-label">Official Website</span>
                      <a href="https://fastinvoice.in" target="_blank" rel="noopener noreferrer" className="contact-card-link">
                        https://fastinvoice.in
                      </a>
                    </div>
                  </div>

                  <div className="contact-card-item">
                    <Mail className="contact-card-icon" />
                    <div>
                      <span className="contact-card-label">Support Email</span>
                      <div className="email-copy-wrapper">
                        <a href="mailto:support@fastinvoice.in" className="contact-card-link">
                          support@fastinvoice.in
                        </a>
                        <button 
                          className="copy-btn" 
                          onClick={handleCopyEmail}
                          title="Copy Email Address"
                        >
                          {copied ? <CheckCircle size={16} className="copied-icon" /> : <Copy size={16} />}
                          <span className="copy-tooltip">{copied ? "Copied!" : "Copy"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </article>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default TermsConditions;
